import {
  FileIcon,
  Loader2Icon,
  MailIcon,
  PaperclipIcon,
  XCircleIcon,
} from 'lucide-react';
import PostalMime from 'postal-mime';
import { useEffect, useState } from 'react';

import { cn } from '@/lib/utils';
import { Blob } from '@/services/types/blob-types';

interface ParsedEmail {
  from?: { name?: string; address?: string };
  to?: Array<{ name?: string; address?: string }>;
  subject?: string;
  date?: string;
  html?: string;
  text?: string;
  attachments?: Array<{
    filename?: string;
    mimeType?: string;
    size?: number;
  }>;
}

interface EmlPreviewProps {
  file: Blob;
}

export const EmlPreview = ({ file }: EmlPreviewProps) => {
  const [state, setState] = useState<{
    isLoading: boolean;
    error: string | null;
    email: ParsedEmail | null;
  }>({
    isLoading: true,
    error: null,
    email: null,
  });

  useEffect(() => {
    const fetchAndParseEml = async () => {
      setState({ isLoading: true, error: null, email: null });

      try {
        const response = await fetch(file.download_url);
        if (!response.ok) {
          throw new Error('Failed to fetch email file');
        }

        const arrayBuffer = await response.arrayBuffer();
        const parser = new PostalMime();
        const parsed = await parser.parse(arrayBuffer);

        setState({
          isLoading: false,
          error: null,
          email: {
            from: parsed.from,
            to: parsed.to,
            subject: parsed.subject,
            date: parsed.date,
            html: parsed.html,
            text: parsed.text,
            attachments: parsed.attachments?.map((att) => ({
              filename: att.filename ?? undefined,
              mimeType: att.mimeType,
              size:
                att.content instanceof ArrayBuffer
                  ? att.content.byteLength
                  : undefined,
            })),
          },
        });
      } catch (err) {
        setState({
          isLoading: false,
          error: err instanceof Error ? err.message : 'Failed to parse email',
          email: null,
        });
      }
    };

    fetchAndParseEml();
  }, [file.download_url, file.id]);

  const { isLoading, error, email } = state;

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[300px]">
        <Loader2Icon className="h-6 w-6 animate-spin text-primary mb-2" />
        <span className="text-xs text-muted-foreground">
          Loading email...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[300px] gap-2">
        <XCircleIcon className="h-8 w-8 text-destructive/70" />
        <span className="text-sm text-destructive">{error}</span>
      </div>
    );
  }

  if (!email) {
    return null;
  }

  const formatAddress = (addr?: { name?: string; address?: string }) => {
    if (!addr) return '';
    if (addr.name && addr.address) return `${addr.name} <${addr.address}>`;
    return addr.name || addr.address || '';
  };

  return (
    <div className="flex flex-col h-full border rounded-md bg-background overflow-hidden">
      {/* Email headers */}
      <div className="border-b bg-muted/30 p-3 space-y-1.5 flex-shrink-0">
        <div className="flex items-start gap-2">
          <MailIcon className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            {email.subject && (
              <h4 className="font-medium text-sm leading-tight truncate">
                {email.subject}
              </h4>
            )}
            {email.from && (
              <p className="text-xs text-muted-foreground truncate">
                From: {formatAddress(email.from)}
              </p>
            )}
            {email.to && email.to.length > 0 && (
              <p className="text-xs text-muted-foreground truncate">
                To: {email.to.map(formatAddress).join(', ')}
              </p>
            )}
            {email.date && (
              <p className="text-xs text-muted-foreground">
                {new Date(email.date).toLocaleString()}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Email body */}
      <div className="flex-1 overflow-auto">
        {email.html ? (
          <iframe
            srcDoc={email.html}
            className="w-full h-full border-0"
            title="Email content"
            sandbox="allow-same-origin"
          />
        ) : email.text ? (
          <pre className="p-3 text-xs whitespace-pre-wrap font-mono">
            {email.text}
          </pre>
        ) : (
          <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
            No content available
          </div>
        )}
      </div>

      {/* Attachments */}
      {email.attachments && email.attachments.length > 0 && (
        <div className="border-t bg-muted/30 p-2 flex-shrink-0">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1.5">
            <PaperclipIcon className="h-3 w-3" />
            <span>{email.attachments.length} attachment(s)</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {email.attachments.map((att, idx) => (
              <div
                key={idx}
                className={cn(
                  'flex items-center gap-1.5 px-2 py-1 rounded',
                  'bg-background border text-xs'
                )}
              >
                <FileIcon className="h-3 w-3 text-muted-foreground" />
                <span className="truncate max-w-[150px]">
                  {att.filename || 'Unnamed'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
